import { observer } from "mobx-react";
import { useParams } from "react-router";
import { PageBreadcrumbs } from "../Breadcrumbs/PageBreadcrumbs";
import { useAppSettingStore } from "./SettingStores/AppSettingPageStore";
import classNames from "classnames";
import { useEffect, useState } from "react";
import HandleEvents from "Util/HandleEvents";
import { Colors, Display, Sizes } from "../Button/Button";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { CreditProviderProductForm } from "./Gibs/CreditProviderProductForm";
import { OrganisationProviderProductList } from "./Gibs/OrganisationProviderProductList";

type availableSection = "details" | "organisations";

export const CreditProviderProductDetail = observer(() => {
	const { providerId, productId } = useParams<{ providerId: string, productId: string }>();
	const { creditProviderItemStore, creditProviderProductStore } = useAppSettingStore();

	const [sectionsToShow, setSectionsToShow] = useState<availableSection[]>(["details"]);

	const handleSectionToggle = (section: availableSection) => {
		if (sectionsToShow.includes(section)) {
			setSectionsToShow(sectionsToShow.filter(s => s !== section));
		} else {
			setSectionsToShow([...sectionsToShow, section]);
		}
	}

	const renderSection = (section: availableSection, title: string | React.ReactNode, content: React.ReactNode) => (
		<div className="credit-provider__section">
			<div
				className={classNames("section-label", { active: sectionsToShow.includes(section) })}
				{...HandleEvents(() => handleSectionToggle(section))}
			>
				{typeof(title) === "string" ? <h6>{title}</h6> : title}
			</div>
			{sectionsToShow.includes(section) && (
				<div className="section-panel">
					{content}
				</div>
			)}
		</div>
	);

	useEffect(() => {
		if (!!productId && !!providerId) {
			creditProviderProductStore.setCreditProviderId(providerId);
			creditProviderProductStore.getProviderProduct(productId);
		}

		return () => {
			creditProviderProductStore.reset();
		}

	}, [productId, creditProviderProductStore, providerId]);

	return (
		<>
			<PageBreadcrumbs
				tags={
					[
						{
							label: !!creditProviderItemStore.creditProvider.providerTypeName ? creditProviderItemStore.creditProvider.providerTypeName : "Reporting",
							link: "/hub/settings/credit-providers",
						},
						{
							label: creditProviderItemStore.creditProvider.name,
							link: `/hub/settings/credit-providers/${creditProviderItemStore.creditProvider.id}`,
						},
						{
							label: creditProviderProductStore.creditProviderProducts.find(product => product.id === productId)?.name ?? "Product Detail",
							link: `/hub/settings/credit-providers/${providerId}/products/${productId}`,
						}
					]
				}
			/>
			{renderSection(
				"details",
				"Product Details",
				<>
					<CreditProviderProductForm />
					<div className="actions">
						<ButtonAsyncState
							display={Display.Solid}
							colors={Colors.Primary}
							sizes={Sizes.Medium}
							onPress={async() =>await creditProviderProductStore.saveProviderProduct()}
						>
							Save Changes
						</ButtonAsyncState>
					</div>
				</>
			)}
			{renderSection(
				"organisations",
				"Linked Organisations",
				<>
					{creditProviderProductStore.selectedProviderProduct?.isActive && (
						<OrganisationProviderProductList productId={productId} providerId={providerId}/>
					)}
					{!creditProviderProductStore.selectedProviderProduct?.isActive && (
						<p className="alert alert__error">
							This product is not active and cannot be linked to any organisations.
						</p>
					)}
				</>
			)}
		</>
	);
});